import { useCallback, useEffect, useState } from "react";
import { Save, RotateCcw, MousePointer } from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";

type CtaContent = {
    enabled: boolean;
    headline: string;
    body: string;
    primary_label: string;
    secondary_label: string;
    primary_target_type: "kontakt" | "external";
    primary_target_url: string;
    auto_dismiss_seconds: number;
    trigger_photo360: boolean;
    trigger_video360: boolean;
    trigger_before_after: boolean;
};

const DEFAULTS: CtaContent = {
    enabled: true,
    headline: "Beeindruckt?",
    body: "Wir dokumentieren auch Ihr Objekt so — unverbindlich, diskret.",
    primary_label: "Projekt anfragen",
    secondary_label: "Weiter schauen",
    primary_target_type: "kontakt",
    primary_target_url: "/#kontakt",
    auto_dismiss_seconds: 12,
    trigger_photo360: true,
    trigger_video360: true,
    trigger_before_after: false,
};

/** Admin-Editor für den Post-Viewer-CTA. Speichert direkt in
 *  `site_content.viewer_cta` (single-key, kein Nested-Objekt-Sync). */
export default function AdminViewerCtaPage() {
    const [content, setContent] = useState<CtaContent>(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api<{ items: Record<string, unknown> }>("/api/admin/site-content");
        if (res.ok && res.data) {
            const raw = (res.data.items?.["viewer_cta"] || {}) as Partial<CtaContent>;
            setContent({ ...DEFAULTS, ...raw });
        }
        setLoading(false);
        setDirty(false);
    }, []);

    useEffect(() => { void load(); }, [load]);

    const patch = <K extends keyof CtaContent>(k: K, v: CtaContent[K]) => {
        setContent((prev) => ({ ...prev, [k]: v }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        const res = await api("/api/admin/site-content/viewer_cta", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: content }),
        });
        setSaving(false);
        if (res.ok) {
            setDirty(false);
            toast.success("360°-CTA gespeichert.");
        } else {
            toast.error(res.error || "Speichern fehlgeschlagen.");
        }
    };

    const resetToDefaults = () => {
        if (!window.confirm("Standardwerte wiederherstellen? (Speichern erst danach)")) return;
        setContent({ ...DEFAULTS });
        setDirty(true);
    };

    if (loading) return <div className="text-hf-muted text-sm">Wird geladen…</div>;

    return (
        <div data-testid="admin-viewer-cta">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="hf-eyebrow">Nach dem 360°-Erlebnis</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">
                        <MousePointer size={22} className="inline-block mr-2 opacity-70" />
                        360°-CTA (&bdquo;Beeindruckt?&ldquo;)
                    </h1>
                    <p className="text-hf-muted text-sm mt-2 max-w-2xl">
                        Erscheint einmal pro Session nach dem ersten Schließen eines 360°-Viewers,
                        um Besucher sanft zum Kontakt zu leiten. Nicht aufdringlich, dezent, editorial.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={resetToDefaults}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider rounded-sm"
                        style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))", color: "hsl(var(--hf-muted))" }}
                        data-testid="admin-viewer-cta-reset"
                    >
                        <RotateCcw size={12} /> Standard
                    </button>
                    <button
                        type="button"
                        onClick={() => void save()}
                        disabled={!dirty || saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wider rounded-sm"
                        style={{
                            background: dirty ? "hsl(var(--hf-ink))" : "hsl(var(--hf-surface))",
                            color: dirty ? "hsl(var(--hf-bg))" : "hsl(var(--hf-muted))",
                            cursor: saving ? "wait" : (dirty ? "pointer" : "not-allowed"),
                            opacity: saving ? 0.7 : 1,
                        }}
                        data-testid="admin-viewer-cta-save"
                    >
                        <Save size={12} /> {saving ? "Speichert…" : "Speichern"}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Editor-Spalte */}
                <div
                    className="p-6 rounded-sm space-y-6"
                    style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                >
                    {/* Enabled-Toggle */}
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="admin-viewer-cta-enabled-wrap">
                        <input
                            type="checkbox"
                            checked={content.enabled}
                            onChange={(e) => patch("enabled", e.target.checked)}
                            className="mt-1"
                            data-testid="admin-viewer-cta-enabled"
                        />
                        <div>
                            <div className="text-sm font-medium">Aktiv</div>
                            <div className="text-xs text-hf-muted">
                                Wenn ausgeschaltet, wird die Card nirgends mehr eingeblendet.
                            </div>
                        </div>
                    </label>

                    {/* Texte */}
                    <div className="space-y-4">
                        <TextField
                            label="Headline"
                            value={content.headline}
                            onChange={(v) => patch("headline", v)}
                            testId="admin-viewer-cta-headline"
                        />
                        <TextAreaField
                            label="Body-Text"
                            value={content.body}
                            onChange={(v) => patch("body", v)}
                            testId="admin-viewer-cta-body"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <TextField
                                label="Primary-Button"
                                value={content.primary_label}
                                onChange={(v) => patch("primary_label", v)}
                                testId="admin-viewer-cta-primary-label"
                            />
                            <TextField
                                label="Secondary-Button"
                                value={content.secondary_label}
                                onChange={(v) => patch("secondary_label", v)}
                                testId="admin-viewer-cta-secondary-label"
                            />
                        </div>
                    </div>

                    {/* Zielort */}
                    <div className="space-y-2">
                        <div className="text-xs uppercase tracking-wider text-hf-muted">Zielort Primary-Button</div>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="radio"
                                checked={content.primary_target_type === "kontakt"}
                                onChange={() => {
                                    patch("primary_target_type", "kontakt");
                                    patch("primary_target_url", "/#kontakt");
                                }}
                                data-testid="admin-viewer-cta-target-kontakt"
                            />
                            Kontaktformular auf Startseite
                            <code className="ml-1 text-xs" style={{ background: "hsl(var(--hf-surface))", padding: "1px 6px" }}>/#kontakt</code>
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="radio"
                                checked={content.primary_target_type === "external"}
                                onChange={() => patch("primary_target_type", "external")}
                                data-testid="admin-viewer-cta-target-external"
                            />
                            Externe URL
                        </label>
                        {content.primary_target_type === "external" && (
                            <input
                                type="url"
                                value={content.primary_target_url}
                                onChange={(e) => patch("primary_target_url", e.target.value)}
                                placeholder="https://…"
                                className="w-full px-3 py-2 text-sm rounded-sm"
                                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                                data-testid="admin-viewer-cta-target-url"
                            />
                        )}
                    </div>

                    {/* Auto-Dismiss */}
                    <div>
                        <label className="flex items-center justify-between text-sm">
                            <span>Auto-Dismiss nach</span>
                            <span className="tabular-nums text-hf-muted">{content.auto_dismiss_seconds}s</span>
                        </label>
                        <input
                            type="range"
                            min={5}
                            max={30}
                            step={1}
                            value={content.auto_dismiss_seconds}
                            onChange={(e) => patch("auto_dismiss_seconds", parseInt(e.target.value, 10))}
                            className="w-full mt-1"
                            data-testid="admin-viewer-cta-auto-dismiss"
                        />
                    </div>

                    {/* Trigger */}
                    <div className="space-y-2">
                        <div className="text-xs uppercase tracking-wider text-hf-muted">Wann anzeigen?</div>
                        <TriggerCheckbox
                            label="Nach 360°-Foto"
                            checked={content.trigger_photo360}
                            onChange={(v) => patch("trigger_photo360", v)}
                            testId="admin-viewer-cta-trigger-photo360"
                        />
                        <TriggerCheckbox
                            label="Nach 360°-Video"
                            checked={content.trigger_video360}
                            onChange={(v) => patch("trigger_video360", v)}
                            testId="admin-viewer-cta-trigger-video360"
                        />
                        <TriggerCheckbox
                            label="Nach 2D-Vorher/Nachher-Slider"
                            checked={content.trigger_before_after}
                            onChange={(v) => patch("trigger_before_after", v)}
                            testId="admin-viewer-cta-trigger-before-after"
                        />
                    </div>
                </div>

                {/* Preview-Spalte */}
                <div
                    className="p-6 rounded-sm flex flex-col items-center justify-center min-h-[300px]"
                    style={{
                        background: "hsl(var(--hf-surface))",
                        border: "1px dashed hsl(var(--hf-line))",
                    }}
                    data-testid="admin-viewer-cta-preview"
                >
                    <div className="hf-eyebrow mb-6" style={{ fontSize: 10 }}>Live-Vorschau</div>
                    <StaticPreviewCard content={content} />
                    {!content.enabled && (
                        <div
                            className="mt-4 text-xs px-3 py-1 rounded-sm"
                            style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                        >
                            Aktuell ausgeschaltet — Card wird nirgends eingeblendet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TextField({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId: string }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-hf-muted">{label}</span>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm rounded-sm"
                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                data-testid={testId}
            />
        </label>
    );
}

function TextAreaField({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId: string }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-hf-muted">{label}</span>
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={2}
                className="mt-1 w-full px-3 py-2 text-sm rounded-sm resize-y"
                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
                data-testid={testId}
            />
        </label>
    );
}

function TriggerCheckbox({ label, checked, onChange, testId }: { label: string; checked: boolean; onChange: (v: boolean) => void; testId: string }) {
    return (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={testId}
            />
            {label}
        </label>
    );
}

/** Statische Vorschau der Card — dieselben Styles wie `PostViewerCta`,
 *  aber ohne Session-Guards, ohne Auto-Dismiss, immer sichtbar. */
function StaticPreviewCard({ content }: { content: CtaContent }) {
    return (
        <div
            className="w-full max-w-[440px] rounded-sm px-5 py-4 relative"
            style={{
                background: "#FFFDF9",
                border: "1px solid #D8CDBF",
                boxShadow: "0 12px 32px -12px rgba(30, 25, 20, 0.28), 0 4px 12px -4px rgba(30, 25, 20, 0.14)",
            }}
        >
            <div className="font-display text-lg leading-tight pr-6" style={{ color: "hsl(var(--hf-ink))" }}>
                {content.headline || "\u00a0"}
            </div>
            <p className="text-sm mt-1.5 leading-relaxed" style={{ color: "hsl(var(--hf-muted))" }}>
                {content.body || "\u00a0"}
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded-sm"
                    style={{ background: "hsl(var(--hf-gold-dark))", color: "hsl(var(--hf-bg))", fontWeight: 500 }}
                >
                    {content.primary_label || "Primary"} <span aria-hidden>→</span>
                </span>
                <span
                    className="text-sm px-3 py-2 rounded-sm"
                    style={{ color: "hsl(var(--hf-muted))" }}
                >
                    {content.secondary_label || "Secondary"}
                </span>
            </div>
        </div>
    );
}
